import { Link } from "react-router-dom";
import { ShieldCheck, Leaf, Sparkles, Users, ArrowRight } from "lucide-react";
import { COMPANY } from "../lib/constants";
import CTASection from "../components/sections/CTASection";

const STATS = [
  { value: "2024", label: "Established" },
  { value: "100%", label: "Eco-Friendly" },
  { value: "5★", label: "Client Rating" },
  { value: "24h", label: "Response Time" },
];

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Trust First",
    desc: "Vetted, insured, and uniformed cleaners — predictable, professional, and accountable.",
  },
  {
    icon: Leaf,
    title: "Eco-Conscious",
    desc: "Premium, non-toxic products that are safe for your family, pets, and the environment.",
  },
  {
    icon: Sparkles,
    title: "Detail Obsessed",
    desc: "We focus on the corners, switches, and seams others rush past.",
  },
  {
    icon: Users,
    title: "People-Led",
    desc: "Real conversations, real ownership — no call centers, no scripts, no shortcuts.",
  },
];

const AboutPage = () => {
  return (
    <main data-testid="about-page" className="bg-white">
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-slate-50/60 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">
              About Us
            </div>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-slate-900 leading-[1.05]">
              A cleaning brand built on{" "}
              <span className="font-medium">trust, care, and craft.</span>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-600 leading-relaxed">
              {COMPANY.name} is a professional cleaning company serving {COMPANY.area}.
              Our experienced team delivers fast, reliable, and eco-friendly cleaning
              solutions for both residential and commercial spaces. We are committed
              to providing high-quality service with attention to every detail.
            </p>
          </div>
        </div>
      </section>

      {/* Image + intro */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-luxury">
              <img
                src="https://images.pexels.com/photos/6196239/pexels-photo-6196239.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="M Brothers Cleaning team beside a service van"
                className="w-full h-[420px] sm:h-[520px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-light text-slate-900">
              Local team. <span className="font-medium">Premium standards.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We started M Brothers with one belief: a clean home or workspace
              shouldn't be a luxury — but the experience of getting one should be.
              From your first call to the final walk-through, we focus on doing
              the small things exceptionally well.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Whether you need a recurring residential clean, a hotel-grade Airbnb
              turnover, or a post-construction reset — every visit is delivered
              by a team that genuinely cares.
            </p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {STATS.map((s, i) => (
                <div
                  key={i}
                  data-testid={`stat-${i}`}
                  className="bg-gradient-to-br from-white to-slate-50 border border-slate-100 rounded-xl p-4 text-center shadow-3d"
                >
                  <div className="font-display text-2xl font-medium gradient-text">
                    {s.value}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-24 bg-slate-50/60 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">
              What we stand for
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-light text-slate-900">
              The values behind every clean.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={i}
                  data-testid={`value-${i}`}
                  className="bg-white border border-slate-100 rounded-xl p-6 shadow-luxury hover:shadow-luxury-hover hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-medium text-slate-900">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {v.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="about-quote-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700"
            >
              Book Your Cleaning Today
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default AboutPage;
